import React, { useRef, useEffect, useState } from 'react'
import { gsap } from 'gsap'

import useIsFirstRender from '../../is_first_render.js'
import useScaled from '../../use_scaled.js'

import AudioPlayer from './AudioPlayer.jsx'
import ExperienceText from './Text.jsx'

import _ from 'lodash'

import { startRipple, destroyRipple, setCanvasSize, waterDrop, setDampeningAndRadius } from '../../../../standalone/ripple.js'



const BackArrow = ({ onClick })=>(
	<div className={
			`
				experience-back-arrow
				text-blue-500/85
				m-1 md:m-2
				w-[23px] h-[23px] pl-[2px] pt-[1px]
				lg:pt-[0] lg:w-[52px] lg:h-[52px] lg:pl-[4px]
				rounded-full bg-black/80
				cursor-pointer
				absolute z-[60]
			`
		} onClick={onClick}>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			className={`w-[20px] h-[20px] lg:w-[50px] lg:h-[50px]`}
			viewBox="0 0 512 512"
		>
			<path
				fill="rgb(177, 151, 252)"
				d="M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z"
			/>
		</svg>
	</div>
)


const playFadeAnimation = (element, timeline, onComplete, reverse = false) =>{
	if(timeline.current){
		timeline.current.pause(0).progress(0)
	} else {
		timeline.current = gsap.timeline({ paused: true })

		timeline.current.fromTo(element, {
			opacity: 0,
			z: -500,
			scale: 0.5,
		}, {
			opacity: 1,
			z: 0,
			scale: 1,
			duration: 1,
			ease: 'sine.inOut'
		})
	}

	if (reverse) {
		timeline.current.eventCallback('onReverseComplete', onComplete);
		timeline.current.reverse(0);
	} else {
		timeline.current.eventCallback('onComplete', onComplete);
		timeline.current.play();
	}
}

// TODO:
// useVantas if vantas_effect: prop
// useMainImage to get imageW imageT imageL, etc.
const Experience = ({
	phrase, phrase_rect,
	text, text_rect, text_interaction_zone_rect,
	song, song_rect,
	title,
	image,
	is_active,
	r_ref,
	onBack,
	onBeforeBack
}) =>{
	const [phrase_time, setPhraseTime] = useState(false)
	const [enable_overflow, setEnableOverflow] = useState(false)

	const phrase_timeout = useRef(null)
	const phrase_ref = useRef()

	const ripple_interval = useRef(null)

	const local_root = useRef(null)
	const once_clicked_ref = useRef(false)
	const timeline_ref = useRef(null)

	const root_ref = r_ref || local_root
	
	const [
		[ scaled_width, scaled_height, scaled_top, scaled_left ],
		setNaturalSize
	] = useScaled({ root_ref: { current: document.body }, focus_height: 1 })

	const image_ref = useRef()
	const canvas_ref = useRef()
	const canvas_resize_ref = useRef()
	const canvas_waterdrop_ref = useRef()

	const is_first_render = useIsFirstRender()

	// We'll use this function assuming the element is positioned
	// on root_ref, and that his top and left are relatives to it
	// so no need to sum root_ref top and left
	const rectToImageRect = ({ top = 0, left = 0, width = 0, height = 0 }) =>{
		return {
			left: (left / 100) * scaled_width,
			top: (top / 100) * scaled_height,
			width: (width / 100) * scaled_width,
			height: (height / 100) * scaled_height
		}
	}

	useEffect(()=>{
		clearTimeout(phrase_timeout.current)

		if(is_active){
			//gsap.set(interactionsElements(), { opacity: 0 })
		}

		// TODO. For some reason first time animation plays
		// there is a stutter. So we play it a first time even
		// if not active
		if(is_active || is_first_render) {
			//root_ref.current.style.width = `${width}px`
			root_ref.current.style.height = `${scaled_height}px`

			image_ref.current.offsetHeight
			image_ref.current.getBoundingClientRect()

			requestAnimationFrame(()=>{
				playFadeAnimation(image_ref.current, timeline_ref, ()=>{
					setEnableOverflow(is_active)

					if(is_active){

/*
						gsap.to(interactionsElements(), {
							opacity: 1,
							duration: 2,
							ease: 'linear',
							overwrite: 'auto'
						})
*/
						phrase_timeout.current = setTimeout(()=> setPhraseTime(is_active), 1000)
					} else {
						gsap.set(image_ref.current, { opacity: 0 })
					}
				})
			})
		}

		if(!is_active){
			clearTimeout(phrase_timeout.current)
			setEnableOverflow(false)
			//setPhraseTime(false)
		}

	}, [is_active])

	useEffect(() => {
		const phrase = phrase_ref.current

		if(phrase){
			gsap.to(phrase_ref.current, {
				opacity: phrase_time ? 1 : 0,
				duration: 1,
				overwrite: 'auto',
			})
		}

		const canvas = canvas_ref.current

		if(canvas && scaled_width){
			canvas_resize_ref.current = startRipple(canvas, image)
		}

		if(!is_active){
			canvas_resize_ref.current = null
		}
	}, [phrase_time, is_active])

	const onPhraseClick = ()=>{
		setPhraseTime(false)
		setEnableOverflow(false)

		const already_clicked = once_clicked_ref.current

		if(!already_clicked){
			once_clicked_ref.current = true

			if(root_ref.current && is_active){
				destroyRipple()

				/*
				gsap.to(interactionsElements(), {
					opacity: 0,
					duration: 0.8,
					ease: 'linear',
					overwrite: 'auto'
				})
				*/
			}

			onBeforeBack?.()

			playFadeAnimation(image_ref.current, timeline_ref, ()=> {
				once_clicked_ref.current = false
				onBack()
			}, true)
		}
	}

	useEffect(()=>{
		const onPopState = ()=>{
			onPhraseClick()
		}

		window.addEventListener('popstate', onPopState)

		return ()=> window.removeEventListener('popstate', onPopState)
	}, [])

	useEffect(()=>{
		const canvasResize = canvas_resize_ref.current

		clearInterval(canvas_waterdrop_ref.current)

		if(canvasResize && is_active){
			canvasResize()
			setDampeningAndRadius(0.750 + 0.20 * (scaled_width/1920), 1);

			const myWaterDrop = ()=>{
				// TODO: use our rects
				root_ref.current.querySelectorAll('.interaction-zone-inactive').forEach(interaction_zone => {
					const { left, top, width, height } = interaction_zone.getBoundingClientRect()

					if(canvas_waterdrop_ref.current){
						setTimeout(()=>waterDrop(left - scaled_left + width/2, top - scaled_top + height/2), Math.random()*300)
					}
				})
			}

			canvas_waterdrop_ref.current = setInterval(myWaterDrop, 1500)

			myWaterDrop()
		}

		const cleanup = ()=> {
			clearInterval(canvas_waterdrop_ref.current)
			canvas_waterdrop_ref.current = null
		}

		if(!is_active){
			//cleanup()
		}

		return cleanup
	}, [scaled_width, scaled_height, phrase_time, scaled_left, scaled_top, is_active])

	const overflow_class = enable_overflow ? `overflow-hidden` : 'overflow-hidden'

	return (
			<div
				ref={root_ref}
				className={`
					relative
					w-full
					select-none ${overflow_class} scrollbar-hide
					font-cinzel font-bold
					font-small-caps
				`}
				style={{
					width: scaled_width + 'px',
					height: scaled_height + 'px',
					left: `${scaled_left > 0 ? scaled_left : 0}px`,
					top: `${scaled_top > 0 ? scaled_top : 0}px`
				}}
			>
				<img
					ref={image_ref}
					src={image}
					className={`
						experience-image
						absolute
						z-[-1]
					`}
					style={{
						perspective: '1000px',
						transformStyle: 'preserve-3d',
						scale: 0.5,
						z: -500,
						opacity: 0
					}}
					onLoad={({target: { naturalWidth, naturalHeight }})=> {
						setNaturalSize([naturalWidth, naturalHeight])
					}}

					width={`${scaled_width}px`}
					height={`${scaled_height}px`}
				/>

				{is_active && phrase_time && <BackArrow onClick={onPhraseClick} />}

				{is_active && phrase_time && <ExperienceText
					text={text}
					text_rect={text_rect}
					text_interaction_zone_rect={rectToImageRect(text_interaction_zone_rect)}
				/>}
				
				{is_active && <AudioPlayer
					song={song}
					song_rect={song_rect}
					interaction_zone_rect={rectToImageRect({
						width: song_rect.width,
						height: song_rect.height,
						top: -song_rect.height / 2,
						left: -song_rect.width / 2
					})}
				/>}

				{is_active && <p
					ref={phrase_ref}
					className={
						`absolute
						text-white text-center
						drop-shadow-experience2 text-shadow-strong
						text-xxs lg:text-xl xl:text-3xl
						-translate-x-1/2 -translate-y-1/2

						z-10 opacity-0
						`
					}
					style={{
						left: phrase_rect.left + '%',
						top: phrase_rect.top + '%',
						width: phrase_rect.width + '%'
					}}
				>
					{phrase}
				</p>}

				{is_active && phrase_time && <canvas
					ref={canvas_ref}
					width={scaled_width}
					height={scaled_height}
					style={{
						opacity: (is_active && phrase_time) ? 1 : 0,
						zIndex: -1
					}}
					className={'experience-image-ripple-canvas absolute top-0 left-0'}
					>
				</canvas>}
			</div>
	)
}

export default Experience